// src/api/auth.js
//
// Token/refresh-token endpoints for the URL-token auth model.
//
// This app is launched with a short-lived JWT in the URL (see utils/bootstrap.js).
// To keep the session alive without a real login flow we:
//   1. exchange that URL JWT for a long-lived refresh token, then
//   2. exchange the refresh token for fresh access tokens as needed.
//
// Both endpoints live on the V2 trans-service (same base va-portal uses for
// /utility/refreshToken).

import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const TRANS_SERVICE_V2_BASE_URL = process.env.VUE_APP_ENV === 'production'
  ? 'https://api.vibrant-america.com/v2/portal/trans-service'
  : 'https://api.vibrant-america.com/v2/portal/trans-service-st';

// Endpoint that exchanges a valid access token (e.g. the URL JWT) for a
// long-lived refresh token (and a fresh access token alongside it).
const EXCHANGE_TOKEN_FOR_REFRESH_ENDPOINT = '/utility/exchangeTokenForRefresh';
// Endpoint that exchanges a refresh token for a fresh access token.
// (mirrors va-portal's refreshAccessTokenApi)
const REFRESH_ACCESS_TOKEN_ENDPOINT = '/utility/refreshToken';

const authService = axios.create({ baseURL: TRANS_SERVICE_V2_BASE_URL });

authService.interceptors.request.use(
  (config) => {
    config.headers['Content-Type'] = 'application/json';
    config.headers['x-request-id'] = uuidv4();
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Exchange a valid access token (the URL-provided JWT) for a long-lived refresh
 * token. The backend also returns a fresh access token in the same response.
 *
 * response.data shape (expiries are epoch-seconds strings):
 *   { access_token, access_token_expiry, refresh_token, refresh_token_expiry }
 *
 * @param {string} accessToken - the JWT that arrived in the URL.
 * @returns {Promise} axios response.
 */
export function exchangeTokenForRefreshApi(accessToken) {
  return authService.post(
    EXCHANGE_TOKEN_FOR_REFRESH_ENDPOINT,
    {},
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
}

/**
 * Exchange a refresh token for a fresh access token.
 *
 * Expected response.data shape: { token, token_expiry, ... }
 * This endpoint is unauthenticated — it relies on the refresh_token in the body,
 * not a Bearer header.
 *
 * @param {string} refreshToken
 * @returns {Promise} axios response.
 */
export function refreshAccessTokenApi(refreshToken) {
  return authService.post(REFRESH_ACCESS_TOKEN_ENDPOINT, { refresh_token: refreshToken });
}
